import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Button from '../../../../../components/ButtonNew'
import Text from '../../../../../components/Text'
import {
  getEncodedDataFromUrl,
  getThumbnail,
  isInApp,
} from '../../../../../utils/helpers/common'
import FileUploadBottomSheet from '../../../../../components/BottomSheet/FileUploadBottomSheet'
import { documentTypeTitle } from '../../../../../utils/helpers/file'
import { useDispatch, useSelector } from 'react-redux'
import * as actions from '../../../../../store/slices/index'
import UploadRecordSuccessModal from '../../../../../components/Modal/UploadRecordSuccessModal'
import moment from 'moment'
import { getEncryptedText } from '../../../../../api/base'
import DefaultDocumentImg from '../../../../../assets/images/care-plans/default-record-preview.svg'
import Calender from '../../../../../components/Icons/Calender'
import ThreeDotsIcon from '../../../../../assets/icons/three-dots.svg'
import MoreOptionsBottomSheet from '../../../../../components/BottomSheet/MoreOptionsBottomSheet'
import DocumentPreviewModal from '../../../../../components/Modal/DocumentPreviewModal'
import ReasonSheet from '../../../../../components/BottomSheet/ReasonSheet'
import { supportProgramStatus } from '../../../../../constants/support-programs'
import AtomicLoader from '../../../../../components/Loader/AtomicLoader'
import DocumentCardHeader from './DocumentCardHeader'
import postMessageToRN, {
  OPEN_PDF_PREVIEW,
} from '../../../../../utils/helpers/ReactNativeEvents'
import { useParams } from 'react-router-dom'

const getDocumentLink = (document) => {
  if (!document?.document_url) return null
  const url = document.document_url
  return Array.isArray(url) && url.length > 0 ? url[0] : url
}

const DocumentView = React.memo(function DocumentView({
  document,
  documentStatus = 'pending',
  uploadReportType = 'pi',
  visitId = '',
  onUploadSuccess = () => {},
  documentConstObj = {},
  setUploadedDocuments,
  onUploadFailed = () => {},
}) {
  const [thumbnail, setThumbnail] = useState('')
  const [thumbnailLoading, setThumbnailLoading] = useState(true)
  const [uploadAgain, setUploadAgain] = useState(false)
  const [showFileUploadBottomSheet, setShowFileUploadBottomSheet] =
    useState(false)
  const [showMoreOptionsBottomSheet, setShowMoreOptionsBottomSheet] =
    useState(false)
  const [showRejectedReasonSheet, setShowRejectedReasonSheet] = useState(false)
  const [showPreviewDocument, setShowPreviewDocument] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isFileUploading, setIsFileUploading] = useState(false)
  const [localPreview, setLocalPreview] = useState(null)
  const [showLocalPreview, setShowLocalPreview] = useState(false)

  const dispatch = useDispatch()
  const { variant: routeVariant } = useParams()
  const payloadVariant = getEncodedDataFromUrl()?.variant
  const documentUrlRef = useRef(null)

  const docTitle = useMemo(() =>
    documentTypeTitle(documentConstObj?.document_type, true),
  )
  const { loading } = useSelector((state) => state.records)
  const { userDetailsState } = useSelector((s) => s.authentication)
  const patientDetails = userDetailsState?.data || {}

  const documentLink = useMemo(() => getDocumentLink(document), [document])

  // Thumbnail fetch with cleanup
  useEffect(() => {
    const urlKey = document?.document_url?.[0] ?? document?.document_url
    if (!document || !urlKey) {
      setThumbnailLoading(false)
      return
    }

    let cancelled = false
    setThumbnailLoading(true)

    getThumbnail(document)
      .then((res) => {
        if (!cancelled) {
          setThumbnail(res)
        }
      })
      .catch(() => {
        if (!cancelled) setThumbnail(null)
      })
      .finally(() => {
        if (!cancelled) setThumbnailLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [document])

  // Revoke object URL on unmount or when localPreview is replaced
  useEffect(() => {
    return () => {
      if (documentUrlRef.current) {
        URL.revokeObjectURL(documentUrlRef.current)
        documentUrlRef.current = null
      }
    }
  }, [])

  const revokePreviousPreview = useCallback(() => {
    if (documentUrlRef.current) {
      URL.revokeObjectURL(documentUrlRef.current)
      documentUrlRef.current = null
    }
  }, [])

  const handleSuccessClose = () => {
    setShowSuccess(false)
    onUploadSuccess()
  }

  const handleFileUpload = useCallback(
    async ({ fileData }) => {
      try {
        setShowFileUploadBottomSheet(false)
        setShowMoreOptionsBottomSheet(false)
        setIsFileUploading(true)

        const formData = new FormData()

        const payload = {
          ...(uploadAgain && {
            test_type_id: document?.test_type_id,
            patient_records_id: document?.patient_records_id,
          }),
          title: docTitle?.title,
          test_name: docTitle.test_name,
          container_account: process.env.REACT_APP_AZURE_CONTAINER_ACCOUNT,
          document_data: [
            uploadAgain
              ? document?.document_name
              : `New-Record-${moment().unix()}.pdf`,
          ],
          patient_id: patientDetails?.patientID,
          container_name: 'patient-reports',
          extension_type:
            fileData?.[0]?.type !== 'application/pdf' ? 'jpg' : 'pdf',
          meta_data: {
            report_status:
              supportProgramStatus.DOCUMENT_REVIEW_STATUS.UNDER_REVIEW,
            isGLP: '1',
            [uploadReportType]: '1',
          },
          ...(payloadVariant && { variant: payloadVariant }),
        }

        const files = Array.isArray(fileData) ? fileData : [fileData]
        files.forEach((file) => formData.append('file', file))
        formData.append('payload', getEncryptedText(payload))

        const runUpload = () =>
          new Promise((resolve, reject) => {
            dispatch(
              actions.updateHealthRecordRequest({
                payload: formData,
                variant: routeVariant,
                resolve,
                reject,
              }),
            )
          })

        await runUpload()
        const file = files[0]
        revokePreviousPreview()
        const previewUrl = URL.createObjectURL(file)
        documentUrlRef.current = previewUrl
        setLocalPreview(previewUrl)
        setShowLocalPreview(true)
        setUploadedDocuments((prev) => prev + 1)
        setShowSuccess(true)
      } catch (error) {
        console.error('Error while uploading Health Record', error)
        onUploadFailed()
      } finally {
        setIsFileUploading(false)
      }
    },
    [
      document?.test_type_id,
      document?.patient_records_id,
      document?.document_name,
      documentConstObj?.document_type,
      dispatch,
      revokePreviousPreview,
      setUploadedDocuments,
      uploadAgain,
      uploadReportType,
      routeVariant,
      payloadVariant,
      visitId,
    ],
  )

  const openFileUpload = useCallback(
    () => setShowFileUploadBottomSheet(true),
    [],
  )
  const closeFileUpload = useCallback(
    () => setShowFileUploadBottomSheet(false),
    [],
  )
  const openMoreOptions = useCallback(
    () => setShowMoreOptionsBottomSheet(true),
    [],
  )
  const closeMoreOptions = useCallback(
    () => setShowMoreOptionsBottomSheet(false),
    [],
  )
  const openPreview = (document) => {
    if (isInApp()) {
      const link = getDocumentLink(document)
      postMessageToRN(OPEN_PDF_PREVIEW, {
        url: link,
        pageTitle: docTitle?.title,
      })
    } else {
      setShowPreviewDocument(true)
    }
  }
  const closePreview = useCallback(() => setShowPreviewDocument(false), [])
  const openRejectedReason = useCallback(
    () => setShowRejectedReasonSheet(true),
    [],
  )
  const closeRejectedReason = useCallback(
    () => setShowRejectedReasonSheet(false),
    [],
  )

  const handleUploadAgainClick = useCallback(() => {
    setUploadAgain(true)
    setShowFileUploadBottomSheet(true)
  }, [])

  if (isFileUploading || loading) {
    return <AtomicLoader show />
  }

  // Empty state (no document)
  if (!document) {
    return (
      <div>
        <DocumentCardHeader documentStatus={documentStatus} />
        <div className="bg-[#F2F4F7] rounded-b-xl rounded-tr-xl px-4 py-5 space-y-4">
          {showLocalPreview && localPreview && (
            <div className="mb-4">
              <Text
                variant="body"
                size="medium"
                tag="p"
                className="text-[#3D4854] mb-2"
              >
                Preview:
              </Text>
              <img
                src={localPreview}
                alt="Local preview"
                className="w-full h-auto rounded-lg max-h-[124px] max-w-[324px] object-scale-down"
                height={124}
                width={324}
              />
            </div>
          )}
          <Text
            variant="body"
            size="large"
            tag="p"
            className="font-semibold text-[#1F2933] mb-2"
            bold="true"
          >
            {docTitle?.title}
          </Text>
          <Text variant="body" size="medium" tag="p" className="text-[#3D4854]">
            {documentConstObj?.description}
          </Text>
          <Button
            fullWidth
            disabled={isFileUploading}
            loading={isFileUploading}
            onClick={openFileUpload}
            size="small"
          >
            {documentConstObj?.button_text}
          </Button>
        </div>

        <FileUploadBottomSheet
          show={showFileUploadBottomSheet}
          onClose={closeFileUpload}
          onFileUpload={(fileData) => handleFileUpload({ fileData })}
        />
        <UploadRecordSuccessModal
          show={showSuccess}
          onClose={handleSuccessClose}
          title={docTitle?.title}
        />
      </div>
    )
  }

  // Rejected state
  if (documentStatus === 'rejected') {
    return (
      <div>
        <DocumentCardHeader documentStatus={documentStatus} />
        <div className="bg-[#F2F4F7] rounded-b-xl rounded-tr-xl px-4 py-5 space-y-4">
          <Text
            variant="body"
            size="large"
            tag="p"
            className="font-semibold text-[#1F2933] mb-2"
            bold="true"
          >
            {docTitle?.title}
          </Text>
          <Text
            variant="body"
            size="medium"
            tag="p"
            className="text-[#3D4854] mb-10"
          >
            {docTitle?.title} was rejected by your Care Specialist.{' '}
            <span
              className="text-red-500 cursor-pointer"
              onClick={openRejectedReason}
              onKeyDown={(e) => e.key === 'Enter' && openRejectedReason()}
              role="button"
              tabIndex={0}
            >
              View Reason
            </span>
          </Text>
          <Button
            fullWidth
            disabled={isFileUploading}
            loading={isFileUploading}
            onClick={handleUploadAgainClick}
          >
            Upload Again
          </Button>
        </div>

        <FileUploadBottomSheet
          show={showFileUploadBottomSheet}
          onClose={closeFileUpload}
          onFileUpload={(fileData) => handleFileUpload({ fileData })}
        />
        <ReasonSheet
          show={showRejectedReasonSheet}
          onClose={closeRejectedReason}
          reason={document?.record_meta_info?.rejected_reason}
        />
        <UploadRecordSuccessModal
          show={showSuccess}
          onClose={handleSuccessClose}
          title={docTitle?.title}
        />
      </div>
    )
  }

  // Uploaded / under review / approved state
  return (
    <div>
      <DocumentCardHeader documentStatus={documentStatus} />
      <div className="bg-[#F2F4F7] rounded-b-xl rounded-tr-xl px-4 py-2 space-y-4">
        <div className="w-full h-full">
          <button
            type="button"
            className="flex items-center justify-center w-full h-full"
            onClick={() => openPreview(document)}
          >
            {thumbnailLoading ? (
              <div>Loading...</div>
            ) : thumbnail ? (
              <img src={thumbnail} alt="thumbnail" />
            ) : (
              <img
                src={localPreview ? localPreview : DefaultDocumentImg}
                className="w-full h-auto rounded-lg max-h-[124px] max-w-[324px] object-scale-down"
                height={124}
                width={324}
                alt="default-document"
              />
            )}
          </button>

          <div className="pt-2">
            <div className="flex items-center justify-between">
              <Text
                variant="body"
                size="large"
                tag="p"
                className="text-[#1F2933] font-semibold"
                bold="true"
              >
                {docTitle?.title}
              </Text>
              <button
                type="button"
                className="px-2 py-1"
                onClick={openMoreOptions}
                aria-label="More options"
              >
                <img src={ThreeDotsIcon} alt="three-dots" />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <Calender />
              <Text
                variant="body"
                size="medium"
                tag="p"
                className="text-[#3D4854]"
              >
                {document?.created_at}
              </Text>
            </div>
          </div>
        </div>

        <MoreOptionsBottomSheet
          show={showMoreOptionsBottomSheet}
          onClose={closeMoreOptions}
          document={{
            documentName: docTitle?.title,
            documentLink,
          }}
          onUploadAgainClick={handleUploadAgainClick}
          showUploadAgain={documentStatus !== 'approved'}
        />
        <DocumentPreviewModal
          show={showPreviewDocument}
          onClose={closePreview}
          documentName={docTitle?.title}
          documentLink={documentLink}
        />
        <FileUploadBottomSheet
          show={showFileUploadBottomSheet}
          onClose={closeFileUpload}
          onFileUpload={(fileData) => handleFileUpload({ fileData })}
        />
        <UploadRecordSuccessModal
          show={showSuccess}
          onClose={handleSuccessClose}
          title={docTitle?.title}
        />
      </div>
      {/* Info */}
      <div className="rounded-2xl bg-[#FFFAEB] p-4 mt-7">
        <Text variant="body" size="medium" className="text-[#DC6803]">
          Your purchase invoice is under review. Your program will start once it
          is approved
        </Text>
      </div>
    </div>
  )
})

export default DocumentView
